/* ============================================================================
   rcds-rail.js — THE shared rail engine. Dots + counter + adopted arrows.
   Pairs with snippets/rcds-rail.liquid and assets/rcds-rail.css.

   Loaded ONCE from layout/theme.liquid, next to rcds-motion.js:
       <script src="{{ 'rcds-rail.js' | asset_url }}" defer></script>

   ⚠ MIGRATION, REQUIRED, ONE LINE. assets/rcds-home.js already contains a
   homepage-only [data-rcds-rail] arrow engine (initRailNav). This file supersedes
   it. Leave both in place and every arrow click scrolls TWICE (two listeners, one
   step each). In assets/rcds-home.js change:
         function boot() { initRailNav(); }      ->      function boot() {}
   initRailNav's other shortcomings are why it is superseded rather than extended:
   no idempotence guard (a second call double-binds), no re-arm hook, no dots, its
   step() reads firstElementChild (wrong once assets/rcds-facets.js hides a card),
   and it resolves arrows with an UNSCOPED document.querySelector so duplicate keys
   across sections cross-wire.

   PUBLIC SURFACE
     window.rcdsRailInit()            re-arm after any DOM replacement. Idempotent.
     document 'rcds:results-swapped'  auto-handled (assets/rcds-vehicle-swap.js fires it)
     document 'shopify:section:load'  auto-handled (theme editor)

   PROGRESSIVE ENHANCEMENT. The UI block ships with `hidden`. This file removes it
   only after measuring that the track really does overflow. So:
     · JS off / blocked / this file 404s  -> zero dots, zero counters, zero dead
       controls, and the track still scrolls (its own css, untouched).
     · a rail that fits on screen         -> UI stays hidden. Dots on a rail that
       cannot scroll are a lie, and 4 of the client's mocks were exactly that case.

   ASSUMES LTR (US store). scrollLeft is treated as a positive offset.
   ========================================================================== */
(function () {
  'use strict';

  /* Re-entrant safe: a second copy of this file just re-arms the first. */
  if (window.__rcdsRail) {
    if (typeof window.rcdsRailInit === 'function') { window.rcdsRailInit(); }
    return;
  }
  window.__rcdsRail = true;

  var READY_FLAG   = 'rcdsRailReady';   /* dataset key stamped on the TRACK */
  var WINDOW_MAX   = 5;                 /* dot window; overridable per adopter */
  var SETTLE_MS    = 180;               /* aria-live debounce after scroll stops */
  var EDGE_TOL     = 2;                 /* px slop for "at the end" */
  var KEY_RE       = /^[a-z0-9_-]+$/i;

  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* Per-track state. WeakMap so a track removed by a section swap is collected
     along with its cached measurements — no manual teardown, no leak. */
  var S = new WeakMap();

  /* ONE ResizeObserver for the whole page, N targets. Cheaper than N observers,
     and it is the only thing that catches the two resize cases a window 'resize'
     listener misses:
       · sections/rcds-install-tabs.liquid hides the inactive panel with
         display:none, so its track goes 0 -> real width with NO viewport change;
       · assets/rcds-install-tabs.css flips the images rail between
         grid-auto-flow:column and display:flex at the 609px boundary. */
  var ro = ('ResizeObserver' in window) ? new ResizeObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) { schedule(entries[i].target, true); }
  }) : null;

  function arr(list) { return Array.prototype.slice.call(list); }

  function esc(v) {
    if (window.CSS && CSS.escape) { return CSS.escape(v); }
    return KEY_RE.test(v) ? v : null;      /* refuse to build a selector we cannot trust */
  }

  /* ---------------------------------------------------------------------------
     PAIRING. Scoped to the track's own Shopify section, so two instances of the
     same section on one page (or a duplicated key like the best-sellers rail's
     "bs", which renders in either of two Liquid branches) can never cross-wire.
     --------------------------------------------------------------------------- */
  function scopeOf(el) {
    return el.closest('.shopify-section, [data-rcds-rail-scope]') || document;
  }

  function findUI(track) {
    var key = track.getAttribute('data-rcds-rail');
    var scope = scopeOf(track);
    if (key) {
      var k = esc(key);
      if (k) {
        var ui = scope.querySelector('[data-rcds-rail-ui="' + k + '"]');
        if (ui) { return ui; }
      }
    }
    /* Forgiving fallback: the nearest following UI block inside the same section,
       for an adopter that rendered the snippet without a matching key. */
    var all = arr(scope.querySelectorAll('[data-rcds-rail-ui]'));
    for (var i = 0; i < all.length; i++) {
      if (track.compareDocumentPosition(all[i]) & Node.DOCUMENT_POSITION_FOLLOWING) { return all[i]; }
    }
    return null;
  }

  function findArrows(track) {
    var key = track.getAttribute('data-rcds-rail');
    var k = key ? esc(key) : null;
    if (!k) { return { prev: null, next: null }; }
    var scope = scopeOf(track);
    return {
      prev: scope.querySelector('[data-rcds-rail-prev="' + k + '"]'),
      next: scope.querySelector('[data-rcds-rail-next="' + k + '"]')
    };
  }

  /* ---------------------------------------------------------------------------
     SLIDES. Universally the DIRECT ELEMENT CHILDREN of the track. Verified against
     every adopter: .rcds-home-rail__item, .rcds-install__slide, .rcds-grev-card,
     .rcds-recent__item, .rcds-fitem, .rcds-home-trade, .rcds-install__vtile.
     There is no single class to key off, so children it is.

     [hidden] children are excluded because assets/rcds-facets.js filters by
     setting card.hidden = true; a filtered-out card must not own a dot.
     Zero-width children are excluded so an sr-only node or a collapsed panel
     never becomes a phantom slide.
     --------------------------------------------------------------------------- */
  function slidesOf(track) {
    var out = [], c = track.children;
    for (var i = 0; i < c.length; i++) {
      var el = c[i];
      if (el.nodeType !== 1) { continue; }
      if (el.hidden) { continue; }
      if (!el.offsetWidth) { continue; }
      out.push(el);
    }
    return out;
  }

  /* ---------------------------------------------------------------------------
     MEASURE. The only place that reads layout geometry. Caches slide offsets so
     the scroll path never touches offsetLeft.
     --------------------------------------------------------------------------- */
  function measure(track) {
    var st = S.get(track);
    if (!st) { return; }

    var slides = slidesOf(track);
    var n = slides.length;
    var maxScroll = track.scrollWidth - track.clientWidth;

    st.n = n;
    st.maxScroll = maxScroll;
    st.scrollWidth = track.scrollWidth;
    st.offs = [];

    /* NOT SCROLLABLE -> no UI. This is the whole answer to "4 of the mocks show
       dots on grids that do not scroll". A converted grid that still fits, or a
       rail inside a display:none panel, reports maxScroll <= 0 and stays silent. */
    if (n < 2 || maxScroll <= EDGE_TOL) {
      silence(st);
      /* st.n = 0 is deliberate, not a shortcut. Leaving the real count here while
         st.offs stays empty lets a later paint() past the `st.n < 2` guard and read
         st.offs[i] as undefined, which makes every distance NaN. Zeroing it trips
         every downstream guard cleanly. st.dotCount = 0 still forces a rebuild if
         the rail becomes scrollable later. */
      st.n = 0;
      st.dotCount = 0;
      if (st.dots) { st.dots.textContent = ''; }
      st.dotEls = [];
      setArrowState(st, true, true);
      return;
    }

    var origin = slides[0].offsetLeft;
    for (var i = 0; i < n; i++) { st.offs.push(slides[i].offsetLeft - origin); }

    /* REACHABLE STOPS, not slide count. When more than one slide is visible the last
       few leading edges lie BEYOND maxScroll: those dots all clamp to the same scroll
       position, and because activeIndex() end-clamps to the final dot the middle ones
       could never light up. That is a dead control, on exactly the 2.27-card rails the
       client is looking at. Count only the stops the rail can actually reach; the
       counter below still reports the true asset total, which is what "so user know how
       many assets" actually asked for. */
    var stops = 1;
    for (var s2 = 1; s2 < n; s2++) {
      if (st.offs[s2] <= maxScroll + EDGE_TOL) { stops = s2 + 1; }
    }
    /* The END of the rail is always a reachable position, even when no slide's leading
       edge lands exactly there because the final slide sits partly visible. Without this
       a genuinely-scrollable 2-tile rail (Ford videos: maxScroll 371, second edge at 395)
       reports ONE stop and the whole UI hides itself - the rail scrolls but says nothing. */
    if (stops < n && st.offs[stops] > maxScroll) { stops += 1; }
    st.stops = stops;
    if (stops < 2) {
      silence(st);
      st.n = 0; st.dotCount = 0;
      if (st.dots) { st.dots.textContent = ''; }
      st.dotEls = [];
      return;
    }

    if (st.ui) {
      buildDots(st);
      if (st.ui.hidden) { st.ui.hidden = false; }
    }
    paint(track);
  }

  /* ---------------------------------------------------------------------------
     DOTS. Rebuilt only when the count actually changes, so a resize is free.
     One button per SLIDE, not per "page". Deliberate: the client's ask was
     "so user know how many assets", so the denominator must be the asset count.
     It is also the correct stop count — every adopter uses
     scroll-snap-align:start, so leading-edge stops == slides.
     --------------------------------------------------------------------------- */
  /* A rail that goes silent must take its COUNTER with it. Both early returns below
     hide st.ui, which was enough while every counter lived inside that ui block. The
     install tabs are the first section to park the counter somewhere else (on the tab
     row, via data-rcds-rail-for), so hiding the ui left a stale "1 / 13" on screen
     while the Videos tab was showing one video. Hide both together, in one place, so
     the two cannot drift apart again. */
  function silence(st) {
    if (st.ui && !st.ui.hidden) { st.ui.hidden = true; }
    if (st.count && !st.count.hidden) { st.count.hidden = true; }
  }

  function buildDots(st) {
    var count = st.stops || st.n;
    if (st.dotCount === count) { return; }
    st.dotCount = count;

    var frag = document.createDocumentFragment();
    for (var i = 0; i < count; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rcds-rail__dot';
      b.setAttribute('data-rcds-rail-dot', String(i));
      b.setAttribute('aria-label', 'Go to ' + st.noun + ' ' + (i + 1) + ' of ' + count);
      b.tabIndex = -1;                 /* roving tabindex; paint() promotes the active one */
      frag.appendChild(b);
    }
    /* replaceChildren, so a double init cannot duplicate a dot row. */
    st.dots.replaceChildren(frag);
    st.dotEls = arr(st.dots.children);
  }

  /* ---------------------------------------------------------------------------
     ACTIVE SLIDE.

     WHY scroll + rAF AND NOT IntersectionObserver.
     IO answers "which slides are >= X% visible". That is not the question. The
     question is "which slide owns the leading edge", and three things in THIS
     theme make IO the wrong tool for it:

       1. UNEQUAL SLIDE WIDTHS ARE REAL HERE, not hypothetical. Above 609px
          assets/rcds-install-tabs.css sets `.rcds-install__rail{display:flex}` with
          `.rcds-install__slide{flex:0 0 auto}`, and the slides carry natural photo
          ratios (17 measured naturals span 0.75-2.01). With unequal widths a fixed
          threshold means a different visible fraction per slide, so IO's answer
          drifts. Nearest-leading-edge is width-independent.

       2. 2.27 CARDS VISIBLE AT ONCE IS ALSO REAL (the mobile vehicle rail: 135px
          tracks + 10px gap in a 335px box). IO reports 2-3 entries intersecting
          simultaneously and you must still choose between them by geometry — so
          you end up doing the scroll math anyway, only on staler, async data.
          "Current" is defined here as THE FIRST SLIDE AT THE LEADING EDGE, which
          is (a) what a swipe advances, and (b) the exact anchor the browser's own
          scroll-snap uses, since every adopter sets scroll-snap-align:start. Dots
          and snapping therefore agree by construction rather than by tuning.
          Centre semantics would light dot 2 while slides 1, 2 and 3 are all on
          screen, which reads as wrong.

       3. IO CANNOT SAY "YOU ARE AT THE END". With 2.27 visible the last slide's
          leading edge is unreachable, so a pure nearest-edge match would never
          report 13/13 and the counter would stick at 11/13. That needs
          scrollLeft vs scrollWidth - clientWidth, which is scroll math.

     Cost of the chosen approach is one rAF-coalesced callback per frame on the
     ONE rail being scrolled: three reads (scrollLeft, clientWidth, scrollWidth)
     plus an integer scan of a cached array. It is also the pattern already used by
     assets/rcds-motion.js and sections/rcds-home-reviews.liquid, so the theme keeps
     one idiom instead of two.
     --------------------------------------------------------------------------- */
  function activeIndex(track, st) {
    var n = st.stops || st.n;
    if (n < 2) { return 0; }
    var x = track.scrollLeft;
    if (x < 0) { x = -x; }                        /* tolerate an RTL context */

    /* End clamp: the last slide's leading edge is unreachable whenever more than
       one slide is visible. Without this the counter never reaches n / n. */
    if (x >= st.maxScroll - EDGE_TOL) { return n - 1; }

    var best = 0, bd = Infinity;
    for (var i = 0; i < n; i++) {
      var d = st.offs[i] - x;
      if (d < 0) { d = -d; }
      if (d < bd) { bd = d; best = i; }
    }
    return best;
  }

  /* ---------------------------------------------------------------------------
     PAINT. Windowed dots + counter + arrow state. No layout writes that could
     force a synchronous reflow inside the scroll frame (class + attribute only).
     --------------------------------------------------------------------------- */
  function paint(track) {
    var st = S.get(track);
    if (!st || st.n < 2) { return; }

    /* Cheap self-heal: a lazy image finishing, or a facet change, alters
       scrollWidth without resizing the track, so ResizeObserver never fires.
       One read per frame catches it. */
    if (track.scrollWidth !== st.scrollWidth) { measure(track); return; }

    var n = st.stops || st.n;
    var w = Math.min(st.win, n);
    var active = activeIndex(track, st);
    st.active = active;

    /* Sliding window, clamped to the ends. */
    var half = Math.floor(w / 2);
    var start = active - half;
    if (start < 0) { start = 0; }
    if (start > n - w) { start = n - w; }
    var end = start + w;

    var els = st.dotEls;
    for (var i = 0; i < n; i++) {
      var el = els[i];
      if (!el) { continue; }
      var shown = (i >= start && i < end);
      if (el.hidden === shown) { el.hidden = !shown; }

      var isOn = (i === active);
      el.classList.toggle('rcds-rail__dot--on', isOn);
      /* Edge shrink only where there is genuinely more content that way. */
      el.classList.toggle('rcds-rail__dot--edge',
        shown && !isOn && ((i === start && start > 0) || (i === end - 1 && end < n)));

      if (isOn) { el.setAttribute('aria-current', 'true'); }
      else { el.removeAttribute('aria-current'); }
      el.tabIndex = isOn ? 0 : -1;                /* roving tabindex: 1 tab stop per rail */
    }

    /* Counter: the denominator is the TRUE slide total (st.n), never the stop total.
       The dots count reachable stops so that every dot works; the counter answers the
       client's actual question, "so user know how many assets". Those are different
       numbers whenever more than one slide is visible, and conflating them is what
       would make a 13-photo gallery claim it has 5. Shown only once the dots alone can
       no longer convey the total. */
    if (st.count) {
      var total = st.n;
      var wantCount = st.wantCount && total > st.win;
      if (wantCount) {
        var firstVisible = 0;
        for (var fv = 0; fv < st.offs.length; fv++) {
          if (st.offs[fv] <= track.scrollLeft + EDGE_TOL) { firstVisible = fv; }
        }
        var txt = (firstVisible + 1) + ' / ' + total;
        if (st.count.textContent !== txt) { st.count.textContent = txt; }
        if (st.count.hidden) { st.count.hidden = false; }
      } else if (!st.count.hidden) {
        st.count.hidden = true;
      }
    }

    setArrowState(st, active <= 0, active >= n - 1 || track.scrollLeft >= st.maxScroll - EDGE_TOL);
    scheduleAnnounce(st);
  }

  /* ---------------------------------------------------------------------------
     ARROWS. This component owns dots + counter ONLY; it does not own arrows.
     Three bespoke arrow treatments already exist with different visual language
     and different placement — .rcds-vehrail__arrow (44px hairline square, below
     the rail), .rcds-install__nav (44px black circle, absolutely positioned over
     the photos) and .rcds-grev__arw (in a foot row flanking the dots). Unifying
     them is a redesign of three live sections that the client did not ask for,
     and every quote this round was about dots. So arrows are ADOPTED, not owned:
     if the markup carries data-rcds-rail-prev/next="<key>" this drives it,
     otherwise it is left completely alone.

     State is the data-rcds-rail-end ATTRIBUTE, which assets/rcds-home.css already
     styles as opacity:.35. `display` is never touched — see rcds-rail.css §5 for
     why that matters and for the live install-tabs bug it sidesteps.
     --------------------------------------------------------------------------- */
  /* ARROWS ARE NOT OURS. assets/rcds-home.js already ships a keyed
     [data-rcds-rail-prev/next] engine and it is live on the homepage (verified: 1 keyed
     pair there, 0 on the collection pages). Binding arrows here too would scroll twice
     per click. Three other bespoke arrow mechanisms exist as well (data-veh-prev on the
     vehicle rail, install-tabs' own handlers, rcds-grev__arw). Owning only the dots and
     the counter removes that entire class of conflict, and every existing arrow keeps
     working untouched. */
  function setArrowState() { /* intentionally empty - see note above */ }

  /* ---------------------------------------------------------------------------
     ARIA-LIVE. Debounced to scroll-settle. The visible counter repaints every
     frame (it is aria-hidden), the live region is written once the rail stops —
     otherwise a single swipe queues a dozen announcements.
     --------------------------------------------------------------------------- */
  function scheduleAnnounce(st) {
    if (!st.live) { return; }
    if (st.settle) { clearTimeout(st.settle); }
    st.settle = setTimeout(function () {
      st.settle = null;
      if (st.n < 2) { return; }
      st.live.textContent = st.noun.charAt(0).toUpperCase() + st.noun.slice(1) +
        ' ' + (st.active + 1) + ' of ' + (st.stops || st.n);
    }, SETTLE_MS);
  }

  /* ---------------------------------------------------------------------------
     SCHEDULING. One rAF per track per frame.
     --------------------------------------------------------------------------- */
  function schedule(track, remeasure) {
    var st = S.get(track);
    if (!st) { return; }
    if (remeasure) { st.dirty = true; }
    if (st.raf) { return; }
    st.raf = requestAnimationFrame(function () {
      st.raf = 0;
      if (st.dirty) { st.dirty = false; measure(track); }
      else { paint(track); }
    });
  }

  /* ---------------------------------------------------------------------------
     NAVIGATION
     --------------------------------------------------------------------------- */
  function goTo(track, i) {
    var st = S.get(track);
    if (!st || st.n < 2) { return; }
    if (i < 0) { i = 0; }
    if (i > st.n - 1) { i = st.n - 1; }
    track.scrollTo({ left: st.offs[i], behavior: reduce ? 'auto' : 'smooth' });
  }

  function nudge(track, dir) {
    var st = S.get(track);
    if (!st || st.n < 2) { return; }
    goTo(track, activeIndex(track, st) + dir);
  }

  /* ---------------------------------------------------------------------------
     INIT / RE-ARM. Idempotent by two mechanisms:
       · a dataset flag on the TRACK, so a second pass over a live track only
         re-measures instead of re-binding;
       · dots are written with replaceChildren, so even a forced rebuild cannot
         produce a duplicate row.
     After assets/rcds-vehicle-swap.js does `host.innerHTML = incoming.innerHTML`,
     the replaced tracks are gone (listeners and WeakMap entries collected with
     them) and the incoming tracks carry no flag, so they initialise cleanly.
     --------------------------------------------------------------------------- */
  function initTrack(track) {
    if (track.dataset[READY_FLAG] === '1') { schedule(track, true); return; }
    track.dataset[READY_FLAG] = '1';

    var ui = findUI(track);
    var arrows = findArrows(track);
    var win = WINDOW_MAX, noun = 'item', wantCount = true;

    if (ui) {
      var m = parseInt(ui.getAttribute('data-rcds-rail-max'), 10);
      if (m > 0) { win = m; }
      noun = ui.getAttribute('data-rcds-rail-noun') || 'item';
      wantCount = !ui.hasAttribute('data-rcds-rail-nocount');
    }

    S.set(track, {
      ui: ui,
      dots: ui ? ui.querySelector('[data-rcds-rail-dots]') : null,
      /* The counter may be placed ANYWHERE inside the section, not just inside the dot
         wrapper: install-tabs puts it on the tab row so "6 / 12" reads on one line with
         IMAGES | VIDEOS. An adopter opts in by rendering
         <span data-rcds-rail-count data-rcds-rail-for="<key>"> where it wants it; the
         in-wrapper counter stays the default so nothing else has to change. */
      count: (function () {
        var k = track.getAttribute('data-rcds-rail');
        var slot = k ? scopeOf(track).querySelector('[data-rcds-rail-count][data-rcds-rail-for="' + esc(k) + '"]') : null;
        return slot || (ui ? ui.querySelector('[data-rcds-rail-count]') : null);
      })(),
      live: ui ? ui.querySelector('[data-rcds-rail-live]') : null,
      dotEls: [], dotCount: -1,
      prev: arrows.prev, next: arrows.next,
      win: win, noun: noun, wantCount: wantCount,
      n: 0, offs: [], maxScroll: 0, scrollWidth: -1,
      active: 0, raf: 0, settle: null, dirty: false
    });

    /* Per-rail passive scroll listener. Not delegated: `scroll` does not bubble,
       and a document capture-phase listener would fire for every scroller on the
       page (including the window) and then have to filter — more work, not less,
       at the <=4 rails a page actually carries. */
    track.addEventListener('scroll', function () { schedule(track, false); }, { passive: true });

    if (ro) { ro.observe(track); }
    measure(track);
  }

  function init() {
    var tracks = document.querySelectorAll('[data-rcds-rail]');
    for (var i = 0; i < tracks.length; i++) { initTrack(tracks[i]); }
  }

  function remeasureAll() {
    var tracks = document.querySelectorAll('[data-rcds-rail]');
    for (var i = 0; i < tracks.length; i++) { schedule(tracks[i], true); }
  }

  /* ---------------------------------------------------------------------------
     DELEGATED USER INTENT. Exactly three document listeners, forever — they do
     not scale with the number of rails, and they survive DOM replacement without
     any re-binding. Same idiom as assets/rcds-facets.js.
     --------------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) { return; }

    var dot = t.closest('[data-rcds-rail-dot]');
    if (dot) {
      var ui = dot.closest('[data-rcds-rail-ui]');
      var track = ui ? trackForUI(ui) : null;
      if (track) { e.preventDefault(); goTo(track, parseInt(dot.getAttribute('data-rcds-rail-dot'), 10) || 0); }
      return;
    }


    var track = null;
    var inDots = t.closest('[data-rcds-rail-dots]');
    if (inDots) {
      var ui = inDots.closest('[data-rcds-rail-ui]');
      track = ui ? trackForUI(ui) : null;
    } else if (t.matches && t.matches('[data-rcds-rail]')) {
      track = t;
    }
    if (!track || !S.has(track)) { return; }

    var st = S.get(track);
    var k = e.key, i = null;
    if (k === 'ArrowRight') { i = st.active + 1; }
    else if (k === 'ArrowLeft') { i = st.active - 1; }
    else if (k === 'Home') { i = 0; }
    else if (k === 'End') { i = st.n - 1; }
    else { return; }

    e.preventDefault();
    goTo(track, i);

    /* Move focus with the selection so the next keypress continues from here. */
    if (inDots && st.dotEls) {
      var target = st.dotEls[Math.max(0, Math.min(st.n - 1, i))];
      if (target) {
        target.tabIndex = 0;
        target.hidden = false;      /* paint() re-windows immediately after */
        target.focus();
      }
    }
  }, false);

  /* Any control change can alter the slide set: assets/rcds-facets.js filters with
     card.hidden on `change`, and sections/rcds-install-tabs.liquid switches panels
     with a radio `change` (which takes a 0-width track to a real one). One
     listener covers both.

     Gated to checkbox/radio on purpose. Both real triggers are one of those two
     (facet checkboxes, install-tab radios), while a PDP fires `change` for every
     variant select, quantity spinner and contact-form field — none of which can
     alter a slide set. Ungated, each of those would queue a re-measure for all
     four rails on the page. */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || (t.type !== 'checkbox' && t.type !== 'radio')) { return; }
    /* Deferred a tick on purpose. The install-tabs panels are shown by a `:checked ~` CSS
       rule, so at the moment `change` fires the panel this radio reveals has not been laid
       out yet: measuring here reads the OLD geometry and a freshly-revealed rail stays
       silent. Verified on /collections/ford - the Videos rail measured maxScroll 371 once
       visible but 0 when measured synchronously. */
    setTimeout(remeasureAll, 0);
  }, false);

  /* THIRD trigger, and it is neither a checkbox nor a radio: the See All / Vans tabs in
     sections/rcds-home-vehicles.liquid are <button role="tab" data-veh-tab> whose handler
     sets card.hidden on every card and then scrollTo({left:0}). No change event fires, and
     if the rail is already at 0 no scroll event fires either, so nothing would re-measure
     and the dot count would keep describing the unfiltered set. */
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[role="tab"],[data-veh-tab]') : null;
    if (!t) { return; }
    setTimeout(remeasureAll, 0);
  }, false);

  function trackForUI(ui) {
    var key = ui.getAttribute('data-rcds-rail-ui');
    var k = key ? esc(key) : null;
    var scope = ui.closest('.shopify-section, [data-rcds-rail-scope]') || document;
    if (k) {
      var tr = scope.querySelector('[data-rcds-rail="' + k + '"]');
      if (tr) { return tr; }
    }
    var all = arr(scope.querySelectorAll('[data-rcds-rail]'));
    for (var i = all.length - 1; i >= 0; i--) {
      if (all[i].compareDocumentPosition(ui) & Node.DOCUMENT_POSITION_FOLLOWING) { return all[i]; }
    }
    return null;
  }

  /* ---------------------------------------------------------------------------
     RE-ARM HOOKS
     --------------------------------------------------------------------------- */
  window.rcdsRailInit = init;                                     /* mirrors window.rcdsFacetsInit */
  document.addEventListener('rcds:results-swapped', init, false);  /* rcds-vehicle-swap.js fires this */
  document.addEventListener('shopify:section:load', init, false);  /* theme editor */
  /* init, NOT remeasureAll: remeasureAll returns early for any track not already in the
     WeakMap, so a rail added between DOMContentLoaded and load would never initialise. */
  window.addEventListener('load', init, false);
  window.addEventListener('orientationchange', remeasureAll, false);
  if (!ro) { window.addEventListener('resize', remeasureAll, { passive: true }); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
